/**
 * One-time "What's New" announcement modal (spec: surprise/excitement
 * prompt for Eva). Shown on Home when there's an announcement newer than
 * settings.lastAnnouncementSeen; dismissing persists the version so it
 * never shows again (until a future announcement bumps the version).
 *
 * Eva-friendly: Pumpkin, one short line, a peek at the new stickers, one
 * big button. No audio. Any close (button, backdrop, Escape) dismisses
 * via onDismiss so the "seen" version always persists.
 *
 * The Pumpkin pose and the preview block both key off announcement.kind,
 * so a future announcement adds a branch here plus an entry in
 * src/lib/announcements.ts and nothing else.
 */
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { PumpkinFigure } from "@/components/PumpkinFigure";
import { StickerEmblem } from "@/components/StickerEmblem";
import { StreakCounter } from "@/components/StreakCounter";
import type { Announcement } from "@/lib/announcements";
import "./WhatsNewModal.css";

export function WhatsNewModal({
  announcement,
  currentStreak,
  onDismiss,
}: {
  announcement: Announcement | null;
  /** Live daily-streak count, shown in the streak-abc preview. */
  currentStreak: number;
  onDismiss: () => void;
}) {
  return (
    <Modal open={announcement !== null} onClose={onDismiss} titleId="whats-new-title">
      {announcement && (
        <div className="whats-new">
          {announcement.kind === "year-2" ? (
            <PumpkinFigure
              className="whats-new__pumpkin whats-new__pumpkin--tall"
              pose="schoolUniform"
              alt="Pumpkin the pangolin in her school uniform, waving"
            />
          ) : (
            <PumpkinFigure
              className="whats-new__pumpkin"
              pose="presentSticker"
              alt="Pumpkin the pangolin, holding up a sticker"
            />
          )}
          <h2 id="whats-new-title" className="whats-new__title">
            {announcement.headline}
          </h2>
          <p className="whats-new__body">{announcement.body}</p>

          {announcement.kind === "streak-abc" ? (
            <StreakAbcPreview days={currentStreak} />
          ) : announcement.kind === "year-2" ? (
            <SchoolYearPreview />
          ) : (
            <NewStickersPreview />
          )}

          <Button size="large" onClick={onDismiss}>
            Let&rsquo;s go!
          </Button>
        </div>
      )}
    </Modal>
  );
}

/** Preview for the streak + ABC announcement: the new streak counter (with
 *  Eva's live number) above a few of the new ABC letter stickers. */
function StreakAbcPreview({ days }: { days: number }) {
  return (
    <div className="whats-new__streak-abc">
      <StreakCounter days={days} variant="compact" />
      <div className="whats-new__abc-row" aria-hidden="true">
        <StickerEmblem category="alphabet" stickerId="letter_c" name="C" isNew />
        <StickerEmblem category="alphabet" stickerId="letter_o" name="O" isNew />
        <StickerEmblem category="alphabet" stickerId="letter_w" name="W" isNew />
      </div>
    </div>
  );
}

/** Preview for the Year 2 announcement: the freshly earned Year 1 sticker
 *  beside the Year 2 one she'll unlock next summer, dimmed the same way the
 *  My Stickers grid dims it. Says "here's what comes next" without asking
 *  her to read another sentence. */
function SchoolYearPreview() {
  return (
    <div className="whats-new__preview" aria-hidden="true">
      <div className="whats-new__preview-item">
        <StickerEmblem category="school" stickerId="year_1_complete" name="Year 1 Done!" isNew />
      </div>
      <div className="whats-new__preview-item">
        <StickerEmblem category="school" stickerId="year_2_complete" name="Year 2 Done!" locked />
      </div>
    </div>
  );
}

/** Preview for the original new-stickers announcement. */
function NewStickersPreview() {
  return (
    <div className="whats-new__preview" aria-hidden="true">
      <div className="whats-new__preview-item">
        <StickerEmblem category="per_session" stickerId="comeback" name="Comeback!" isNew />
      </div>
      <div className="whats-new__preview-item">
        <StickerEmblem category="alphabet" stickerId="letter_a" name="A" isNew />
      </div>
      <div className="whats-new__preview-item">
        <StickerEmblem category="per_session" stickerId="clean_sweep" name="Clean Sweep!" isNew />
      </div>
    </div>
  );
}
